/**
 * 🚀 [Phase 107.B — 2026-07-14] Widget d'affichage du coût employeur dynamique.
 *
 * Fetch : GET /api/crm/bons-travail/{bt_id}/cout-employeur-dynamique
 *
 * Affiche 3 modes :
 *   - **Entreprise** : "Basé sur la moyenne entreprise (X employés terrain)"
 *   - **Pointés** : "Basé sur X employés pointés" (tooltip liste)
 *   - **Approuvé** : "Approuvé le [date] par [user]" (immuable)
 *
 * Bouton "Approuver le coût employeur" visible si :
 *   - `peut_etre_approuve === true` (mode entreprise ou pointes)
 *   - + Owner / Admin
 *
 * Props :
 *   - btId : string (id du bon de travail)
 *   - currentUser : { role: 'proprietaire' | 'admin' | ... }
 *   - dataTestId : string optionnel (préfixe des data-testid enfants)
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CheckCircle2, Info, Loader2 } from 'lucide-react';
import { API_URL } from './utils';
import { usePermissions } from '@/lib/PermissionsContext';

const MODE_LABELS = {
  entreprise: 'Basé sur la moyenne entreprise',
  pointes: 'Basé sur les employés pointés',
  approuve: 'Coût approuvé',
};

const MODE_COLORS = {
  entreprise: 'bg-slate-100 text-slate-700 border-slate-300',
  pointes: 'bg-blue-50 text-blue-800 border-blue-300',
  approuve: 'bg-emerald-50 text-emerald-800 border-emerald-300',
};

function formatFrCa(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function CoutEmployeurBadge({ btId, currentUser, dataTestId = 'cout-employeur-badge' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState(null);

  // 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Branchement RBAC via `bt_approuver_cout_employeur`.
  // Contrôle : bouton "Approuver le coût employeur". Fail-secure quand permissions non chargées.
  const { canDo } = usePermissions() || {};
  const canApprove = !!(canDo && canDo('bt_approuver_cout_employeur'));

  const fetchData = useCallback(async () => {
    if (!btId) return;
    setLoading(true);
    setError(null);
    try {
      // 🐛 [Bug prod 2026-07-16] AuthContext stocke sous 'auth_token', pas 'token'.
      // Ancien : localStorage.getItem('token') retournait null → "Token invalide" 401
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const r = await axios.get(
        `${API_URL}/api/crm/bons-travail/${btId}/cout-employeur-dynamique`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [btId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async () => {
    if (!btId) return;
    const ok = window.confirm(
      `Approuver ce coût employeur à ${(data?.montant || 0).toFixed(2)} $/h ?\n\n` +
        'Cette valeur sera figée pour ce bon de travail et servira de référence ' +
        'pour le calcul des heures budgétées. Cette action est irréversible.',
    );
    if (!ok) return;
    setApproving(true);
    try {
      // 🐛 [Bug prod 2026-07-16] Idem — lire depuis 'auth_token' (source AuthContext)
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      await axios.post(
        `${API_URL}/api/crm/bons-travail/${btId}/approuver-cout-employeur`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Coût employeur approuvé et figé.');
      fetchData();
    } catch (e) {
      toast.error(
        `Impossible d'approuver le coût : ${e?.response?.data?.detail || e.message}`,
      );
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="flex items-center gap-2 text-xs text-slate-500"
        data-testid={`${dataTestId}-loading`}
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Chargement du coût employeur…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="text-xs text-red-600"
        data-testid={`${dataTestId}-error`}
      >
        Erreur : {error}
      </div>
    );
  }

  if (!data) return null;

  const mode = data.mode;
  const badgeClass = `text-xs px-2 py-1 border ${MODE_COLORS[mode] || MODE_COLORS.entreprise}`;

  const tooltipContent = (
    <div className="text-xs space-y-1 max-w-xs" data-testid={`${dataTestId}-details`}>
      <p className="font-semibold">{MODE_LABELS[mode]}</p>
      {mode === 'approuve' && (
        <>
          <p>Approuvé le : {formatFrCa(data.approuve_le)}</p>
          <p>Par : {data.approuve_par}</p>
        </>
      )}
      {mode === 'entreprise' && (
        <p>Fenêtre : {data.fenetre_jours} jours · {data.nb_employes_terrain_actifs} employés terrain</p>
      )}
      {mode === 'pointes' && (
        <p>Employés : {data.nb_employes_pointes} pointé(s) sur ce BT</p>
      )}
      {Array.isArray(data.employees) && data.employees.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {data.employees.slice(0, 8).map(e => (
            <li key={e.employee_id}>
              • {e.nom_complet} : {e.cout_employeur?.toFixed(2)} $/h
              {e.heures_ponderantes ? ` (${e.heures_ponderantes.toFixed(1)}h)` : ''}
            </li>
          ))}
          {data.employees.length > 8 && (
            <li className="italic text-slate-400">…et {data.employees.length - 8} autre(s)</li>
          )}
        </ul>
      )}
    </div>
  );

  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50/70 px-3 py-2"
      data-testid={dataTestId}
    >
      <div className="flex items-center gap-2 flex-1">
        <span className="text-sm font-medium text-slate-700">
          Coût employeur :
        </span>
        <span
          className="text-lg font-bold text-slate-900"
          data-testid={`${dataTestId}-montant`}
        >
          {(data.montant || 0).toFixed(2)} $/h
        </span>
        <Badge
          className={badgeClass}
          data-testid={`${dataTestId}-mode-${mode}`}
        >
          {MODE_LABELS[mode] || mode}
        </Badge>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-slate-400 hover:text-slate-600"
                data-testid={`${dataTestId}-info-trigger`}
              >
                <Info className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="bg-white shadow-lg border">
              {tooltipContent}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {data.peut_etre_approuve && canApprove && (
        <Button
          size="sm"
          variant="outline"
          onClick={handleApprove}
          disabled={approving}
          className="text-xs"
          data-testid={`${dataTestId}-btn-approuver`}
        >
          {approving ? (
            <>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              Approbation…
            </>
          ) : (
            <>
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Approuver le coût employeur
            </>
          )}
        </Button>
      )}
    </div>
  );
}

export default CoutEmployeurBadge;
